<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import { AlertTriangle, ArrowRight, CalendarClock, X } from "lucide-vue-next";
import { fullDate, shortDate } from "@/utils/dates";
import type { ProjectScheduleMovePreview } from "@/types/models";

const props = defineProps<{
  preview: ProjectScheduleMovePreview | null;
  applying?: boolean;
}>();
const emit = defineEmits<{ close: []; apply: [] }>();
const dialog = ref<HTMLElement | null>(null);
const closeButton = ref<HTMLButtonElement | null>(null);
const visibleChanges = computed(() => props.preview?.changes.slice(0, 8) || []);
const terminalMoveCount = computed(
  () =>
    props.preview?.warnings.filter(
      (warning) => warning.code === "terminal_item_date_moved",
    ).length || 0,
);
let returnFocus: HTMLElement | null = null;
let priorOverflow = "";

watch(
  () => props.preview,
  async (preview) => {
    if (preview) {
      returnFocus = document.activeElement as HTMLElement;
      priorOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      await nextTick();
      closeButton.value?.focus();
    } else {
      document.body.style.overflow = priorOverflow;
      returnFocus?.focus();
    }
  },
);
onBeforeUnmount(() => {
  document.body.style.overflow = priorOverflow;
});

function trapKeys(event: KeyboardEvent) {
  if (event.key === "Escape" && !props.applying) {
    event.preventDefault();
    emit("close");
    return;
  }
  if (event.key !== "Tab" || !dialog.value) return;
  const items = [
    ...dialog.value.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ];
  if (!items.length) return;
  if (event.shiftKey && document.activeElement === items[0]) {
    event.preventDefault();
    items.at(-1)?.focus();
  } else if (!event.shiftKey && document.activeElement === items.at(-1)) {
    event.preventDefault();
    items[0]?.focus();
  }
}
</script>

<template>
  <Teleport to="body">
    <div v-if="preview" class="move-layer">
      <button
        class="move-scrim"
        type="button"
        aria-label="Keep current project schedule"
        :disabled="applying"
        @click="emit('close')"
      ></button>
      <section
        ref="dialog"
        class="move-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="move-title"
        @keydown="trapKeys"
      >
        <header>
          <span class="move-icon"><CalendarClock :size="21" /></span>
          <div>
            <h2 id="move-title">Review project schedule move</h2>
            <p>
              {{ preview.changes.length }} work
              {{
                preview.changes.length === 1 ? "item changes" : "items change"
              }}
              with the new project start.
            </p>
          </div>
          <button
            ref="closeButton"
            class="icon-button"
            type="button"
            aria-label="Close project schedule preview"
            :disabled="applying"
            @click="emit('close')"
          >
            <X :size="20" />
          </button>
        </header>
        <div
          class="move-body"
          role="region"
          aria-label="Changed schedule details"
          tabindex="0"
        >
          <dl class="project-changes">
            <div>
              <dt>Project start</dt>
              <dd>
                <time>{{ fullDate(preview.oldProjectStartDate) }}</time>
                <ArrowRight :size="16" />
                <strong>{{
                  fullDate(preview.proposedProjectStartDate)
                }}</strong>
              </dd>
            </div>
            <div>
              <dt>Project target</dt>
              <dd>
                <time>{{ fullDate(preview.oldProjectTargetEndDate) }}</time>
                <ArrowRight :size="16" />
                <strong>{{
                  fullDate(preview.proposedProjectTargetEndDate)
                }}</strong>
              </dd>
            </div>
            <div>
              <dt>Working days</dt>
              <dd>
                {{ preview.weekdaysOnly ? "Monday–Friday" : "Every day" }}
              </dd>
            </div>
          </dl>

          <div v-if="terminalMoveCount" class="move-warning">
            <AlertTriangle :size="19" />
            <div>
              <strong>Completed or closed work is included.</strong>
              <p>
                {{ terminalMoveCount }} completed, not-applicable, or cancelled
                {{ terminalMoveCount === 1 ? "item moves" : "items move" }}.
                Review this carefully because these dates are part of project
                history.
              </p>
            </div>
          </div>
          <div
            v-if="!preview.canApply"
            class="move-warning move-warning--error"
            role="alert"
          >
            <AlertTriangle :size="19" />
            <div>
              <strong>This schedule move cannot be applied.</strong>
              <p>Resolve the schedule conflict, then create a new preview.</p>
            </div>
          </div>

          <section v-if="preview.changes.length" class="changed-work">
            <h3>Changed work</h3>
            <div v-for="item in visibleChanges" :key="item.taskId">
              <span>
                <strong>{{ item.title }}</strong>
                <small>{{
                  item.kind === "summary" ? "Task collection" : item.kind
                }}</small>
              </span>
              <span class="tabular">
                {{ shortDate(item.oldStartDate || "") }}–{{
                  shortDate(item.oldDueDate || "")
                }}
                <ArrowRight :size="14" />
                <strong
                  >{{ shortDate(item.startDate || "") }}–{{
                    shortDate(item.dueDate || "")
                  }}</strong
                >
              </span>
            </div>
            <p
              v-if="preview.changes.length > visibleChanges.length"
              class="more-count"
            >
              And {{ preview.changes.length - visibleChanges.length }} more
              changed work
              {{
                preview.changes.length - visibleChanges.length === 1
                  ? "item"
                  : "items"
              }}.
            </p>
          </section>
          <p class="notification-note">
            Dates and the project target update together. Task durations,
            dependencies, assignments, and baseline dates stay unchanged.
          </p>
        </div>
        <footer>
          <button
            class="button button--secondary"
            type="button"
            :disabled="applying"
            @click="emit('close')"
          >
            Keep current schedule
          </button>
          <button
            class="button button--primary"
            type="button"
            :disabled="applying || !preview.canApply"
            @click="emit('apply')"
          >
            {{ applying ? "Applying schedule move…" : "Apply schedule move" }}
          </button>
        </footer>
      </section>
    </div>
  </Teleport>
</template>

<style scoped>
.move-layer {
  position: fixed;
  inset: 0;
  z-index: 130;
  display: grid;
  place-items: center;
  padding: 20px;
}
.move-scrim {
  position: absolute;
  inset: 0;
  width: 100%;
  background: rgba(8, 27, 46, 0.52);
}
.move-dialog {
  position: relative;
  width: min(700px, 100%);
  max-height: calc(100vh - 40px);
  display: grid;
  grid-template-rows: auto 1fr auto;
  overflow: hidden;
  border-radius: 10px;
  background: white;
  box-shadow: var(--shadow-panel);
}
.move-dialog > header {
  display: grid;
  grid-template-columns: 40px 1fr auto;
  gap: 12px;
  padding: 20px 22px;
  border-bottom: 1px solid var(--rule);
}
.move-dialog header p,
.more-count {
  margin-top: 3px;
  color: var(--text-soft);
}
.move-icon {
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  border-radius: 7px;
  color: var(--shared);
  background: var(--shared-soft);
}
.move-body {
  overflow: auto;
  display: grid;
  gap: 18px;
  padding: 22px;
}
.project-changes {
  display: grid;
  gap: 10px;
}
.project-changes > div {
  display: grid;
  grid-template-columns: 130px 1fr;
  gap: 14px;
}
.project-changes dt {
  color: var(--text-soft);
  font-weight: 700;
}
.project-changes dd {
  display: flex;
  align-items: center;
  gap: 8px;
}
.project-changes dd strong {
  color: var(--shared);
}
.move-warning {
  display: flex;
  gap: 11px;
  padding: 13px;
  border-radius: 6px;
  color: #70470c;
  background: var(--warning-soft);
}
.move-warning--error {
  color: #7d2828;
  background: #fff0f0;
}
.move-warning svg {
  flex: 0 0 auto;
}
.move-warning p {
  margin-top: 3px;
}
.changed-work {
  display: grid;
}
.changed-work h3 {
  margin-bottom: 8px;
}
.changed-work > div {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  padding: 10px 0;
  border-top: 1px solid var(--rule);
}
.changed-work > div > span:first-child {
  display: grid;
}
.changed-work small {
  color: var(--text-soft);
  text-transform: capitalize;
}
.changed-work > div > span:last-child {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}
.notification-note {
  padding: 11px 12px;
  border-radius: 6px;
  color: #174f7c;
  background: var(--shared-soft);
}
.move-dialog > footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 13px 22px;
  border-top: 1px solid var(--rule);
  background: var(--sheet-subtle);
}
@media (max-width: 600px) {
  .move-layer {
    padding: 0;
  }
  .move-dialog {
    width: 100%;
    height: 100%;
    max-height: none;
    border-radius: 0;
  }
  .project-changes > div {
    grid-template-columns: 1fr;
    gap: 3px;
  }
  .changed-work > div {
    align-items: flex-start;
    flex-direction: column;
  }
  .move-dialog > footer {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
  .move-dialog .button {
    height: auto;
  }
}
</style>
